import React, { useCallback, useState } from 'react';
import { Stack, Typography } from '@mui/material';
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { type TFunction } from 'i18next';

import { type LocationDTO, useDeleteRoleAssignment, type UserLocationRoleAssignmentDto } from '@/api/generated';
import EllipsisHorizontalIcon from '@/assets/icons/ellipsis-horizontal.svg?react';
import Badge from '@/components/Badge';
import ConfirmationModal from '@/components/ConfirmationModal/ConfirmationModal';
import BaseCell from '@/components/DataGrid/BaseCell';
import DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import LocationMultiDisplay from '@/components/fields/Location/LocationMultiDisplay';
import RoleDisplay from '@/containers/Users/RoleDisplay';
import { useReadonly } from '@/hooks/useReadonly';

const columnHelper = createColumnHelper<UserLocationRoleAssignmentDto>();

export const clientUserAccessRolesColumns = (t: TFunction) =>
  [
    columnHelper.accessor('location', {
      header: () => t('users.columns.item'),
      cell: ({ getValue, row }) => {
        const location = getValue();
        const locations = [location!].filter(Boolean);

        return (
          <BaseCell>
            <LocationMultiDisplay
              locations={locations as LocationDTO[]}
              organization={row.original.organization}
              sx={{
                '& .MuiStack-root.LocationItem-root': {
                  border: 'none'
                },
                '& .MuiStack-root.LocationItem-root .LocationItem-label': {
                  maxWidth: 200
                }
              }}
            />
          </BaseCell>
        );
      },
      enableSorting: false
    }),
    columnHelper.accessor('organization', {
      header: () => t('users.columns.organization'),
      cell: ({ getValue }) => {
        const organization = getValue();

        return (
          <BaseCell>
            <Stack direction="row" alignItems="center" gap={1}>
              <Badge variant="standard" color="primary">
                {organization?.name}
              </Badge>
            </Stack>
          </BaseCell>
        );
      },
      enableSorting: false
    }),
    columnHelper.display({
      id: 'roles',
      header: () => t('users.columns.role'),
      cell: ({ row }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const { isReadonly } = useReadonly();
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const { mutateAsync: deleteRoleAssignment, isLoading: isDeleting } = useDeleteRoleAssignment();
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [roleAssignmentToRemove, setRoleAssignmentToRemove] = useState<UserLocationRoleAssignmentDto | null>(
          null
        );

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const handleDeleteClick = useCallback(() => {
          setRoleAssignmentToRemove(row.original);
        }, [row.original]);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const handleDeleteConfirm = useCallback(async () => {
          if (roleAssignmentToRemove) {
            await deleteRoleAssignment({ roleAssignmentId: roleAssignmentToRemove.id! });
            setRoleAssignmentToRemove(null);
          }
        }, [deleteRoleAssignment, roleAssignmentToRemove]);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const handleDeleteCancel = useCallback(() => {
          setRoleAssignmentToRemove(null);
        }, []);

        const roles = t('list', { val: row?.original?.roles?.map(role => role.name), ns: 'common' });
        const location = row.original.location?.name;
        const organization = row.original.organization?.name;

        return (
          <BaseCell sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <RoleDisplay value={row.original.roles} />
            {!isReadonly && (
              <>
                <DropdownMenu
                  customIcon={<EllipsisHorizontalIcon />}
                  menuProps={{
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'right'
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'right'
                    }
                  }}
                  autoClose
                >
                  <DropdownMenuItem
                    text={t('users.actions.delete')}
                    onClick={handleDeleteClick}
                    disabled={false}
                    sx={{ color: 'error.main' }}
                  />
                </DropdownMenu>
                <ConfirmationModal
                  open={!!roleAssignmentToRemove}
                  onConfirm={handleDeleteConfirm}
                  onDiscard={handleDeleteCancel}
                  title={
                    <Stack>
                      <Typography variant="body2">{t('users.deleteRoleAssignment.confirmation')}</Typography>
                      <Typography variant="body2">
                        {t('users.deleteRoleAssignment.assignmentDetails', { roles, location, organization })}
                      </Typography>
                    </Stack>
                  }
                  confirmButtonLabel={t('users.deleteRoleAssignment.confirm')}
                  isLoading={isDeleting}
                  loadingLabel={t('users.deleteRoleAssignment.deleting')}
                />
              </>
            )}
          </BaseCell>
        );
      },
      enableSorting: false
    })
  ] as ColumnDef<UserLocationRoleAssignmentDto>[];
